'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

export function NavigationLoader() {
  const pathname = usePathname();
  const [targetPath, setTargetPath] = useState<string | null>(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const [lastPathname, setLastPathname] = useState(pathname);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset loader state when pathname changes — handled during render (not in an effect)
  // so it covers both <a> arrivals AND programmatic navigations to a different URL than armed.
  // Pattern: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    setTargetPath(null);
    setShowSpinner(false);
  }

  // Pending timers are cleared in the effect cleanup below (refs can't be touched during render).
  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, [pathname]);

  const visible = showSpinner && targetPath !== null && targetPath !== pathname;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Ignore cmd/ctrl/shift+click (open in new tab, etc.)
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      // Only internal links, not anchors or same page
      if (!href || !href.startsWith('/') || href.startsWith('/#')) return;
      if (href.split('?')[0] === pathname) return;

      // Cancel any previous pending navigation
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);

      setTargetPath(href.split('?')[0]);
      setShowSpinner(false);

      // Only show after 600ms — fast navigations stay invisible
      showTimerRef.current = setTimeout(() => setShowSpinner(true), 600);
      // Safety fallback: auto-hide after 15s
      safetyTimerRef.current = setTimeout(() => {
        setShowSpinner(false);
        setTargetPath(null);
      }, 15_000);
    };

    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, [pathname]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm font-medium text-white">Chargement…</p>
      </div>
    </div>
  );
}
